import { Injectable, Inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Observable, from, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay } from 'rxjs/operators';
import { AuthorisationService } from '../authorisation/authorisation.service';

@Injectable({
  providedIn: 'root'
})
export class SignalrService {
  private candidateHubConnection?: signalR.HubConnection;
  private connection$?: Observable<void>;

  constructor(@Inject('signalRBaseUrl') private signalRBaseUrl: string,
              private authorisationService: AuthorisationService) { }

  /**
   * Opens the candidate hub connection on the first call and shares the result with
   * every later caller via shareReplay. The replay is what makes this safe to
   * call from any screen: a component that subscribes *after* the connection was
   * opened (e.g. the paused screen, which is always reached from the running
   * exam) still receives the emission and can register its handlers
   */
  public startCandidateConnection(): Observable<void> {
    if (!this.connection$) {
      this.candidateHubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.signalRBaseUrl}/candidate`, {
          transport: signalR.HttpTransportType.WebSockets,
          skipNegotiation: true
        })
        .withAutomaticReconnect()
        .build();

      this.connection$ = from(this.candidateHubConnection.start())
        .pipe(mergeMap(() => from(this.candidateHubConnection!.invoke('Initialise', this.authorisationService.getAccessToken()))))
        .pipe(map(() => undefined))
        .pipe(catchError(() => of(undefined)))
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }

    return this.connection$;
  }

  /**
   * Exposes a hub event as an observable. The handler is attached on subscribe
   * and detached on unsubscribe, so callers can compose it declaratively
   * instead of registering imperatively.
   */
  public on<T = void>(method: string): Observable<T> {
    return new Observable<T>(subscriber => {
      const handler = (payload: T) => subscriber.next(payload);
      this.candidateHubConnection?.on(method, handler);

      return () => this.candidateHubConnection?.off(method, handler);
    });
  }

  /**
   * Imperative single-handler subscription, retained for existing callers.
   * Replaces any previously registered handler for the same event.
   */
  public subscribe(method: string, callback: (...params: any) => any): void {
    if (this.candidateHubConnection) {
      this.candidateHubConnection.off(method);
      this.candidateHubConnection.on(method, callback);
    }
  }
}
